import * as XLSX from 'xlsx';
import { Payment } from '@/types/payment.types';

export interface ExportOptions {
  scope: 'current-page' | 'all-filtered';
  filename?: string;
}

class ExportService {

  private getStudentNames(payment: Payment): string {
    if (!payment.concepts || payment.concepts.length === 0) {
      return 'N/A';
    }

    const studentIds = payment.concepts
      .map((concept) => concept.studentId)
      .filter((id): id is string => !!id);

    if (studentIds.length === 0) {
      return 'N/A';
    }

    const uniqueIds = [...new Set(studentIds)];
    
    return uniqueIds.join(', ');
  }


  private getConceptsDescription(payment: Payment): string {
    if (!payment.concepts || payment.concepts.length === 0) {
      return 'N/A';
    }

    return payment.concepts
      .map((concept) => `${concept.name} ($${concept.amount.toLocaleString('es-CO')})`)
      .join(', ');
  }


  private formatPaymentForExport(payment: Payment) {
    return {
      Fecha: new Date(payment.createdAt).toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      Acudiente: payment.customerName || 'N/A',
      Email: payment.customerEmail || 'N/A',
      Teléfono: payment.customerPhone || 'N/A',
      Sede: payment.site || 'N/A',
      'ID Estudiante(s)': this.getStudentNames(payment),
      Conceptos: this.getConceptsDescription(payment),
      Monto: `$${payment.amount.toLocaleString('es-CO')}`,
      Estado: this.translateStatus(payment.status),
      'Código Referencia': payment.referenceCode || 'N/A',
      'ID Transacción': payment.id,
    };
  }


  private translateStatus(status: Payment['status']): string {
    const statusMap: Record<Payment['status'], string> = {
      pending: 'Pendiente',
      completed: 'Completado',
      approved: 'Aprobado',
      failed: 'Fallido',
      rejected: 'Rechazado',
    };
    return statusMap[status] || status;
  }


  private generateFilename(prefix: string = 'reporte-pagos'): string {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, -5);
    return `${prefix}_${timestamp}.xlsx`;
  }


  exportToExcel(
    payments: Payment[],
    options: ExportOptions = { scope: 'current-page' }
  ): void {
    if (payments.length === 0) {
      throw new Error('No hay datos para exportar');
    }

    const formattedData = payments.map((payment) =>
      this.formatPaymentForExport(payment)
    );

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pagos');

    const columnWidths = [
      { wch: 18 }, // Fecha
      { wch: 25 }, // Acudiente
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 20 }, // Sede
      { wch: 20 }, // ID Estudiante(s)
      { wch: 40 }, // Conceptos
      { wch: 15 }, // Monto
      { wch: 12 }, // Estado
      { wch: 20 }, // Código Referencia
      { wch: 25 }, // ID Transacción
    ];
    worksheet['!cols'] = columnWidths;

    const filename = options.filename || this.generateFilename();

    XLSX.writeFile(workbook, filename);
  }


  exportToExcelWithMetadata(
    payments: Payment[],
    metadata: {
      totalAmount: number;
      filters?: string;
      dateRange?: string;
    },
    options: ExportOptions = { scope: 'current-page' }
  ): void {
    if (payments.length === 0) {
      throw new Error('No hay datos para exportar');
    }

    const workbook = XLSX.utils.book_new();

    const formattedData = payments.map((payment) =>
      this.formatPaymentForExport(payment)
    );
    const dataSheet = XLSX.utils.json_to_sheet(formattedData);
    dataSheet['!cols'] = [
      { wch: 18 }, // Fecha
      { wch: 25 }, // Acudiente
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 20 }, // Sede
      { wch: 20 }, // ID Estudiante(s)
      { wch: 40 }, // Conceptos
      { wch: 15 }, // Monto
      { wch: 12 }, // Estado
      { wch: 20 }, // Código Referencia
      { wch: 25 }, // ID Transacción
    ];
    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Pagos');

    const summaryData = [
      { Campo: 'Total de registros', Valor: payments.length },
      {
        Campo: 'Monto total',
        Valor: `$${metadata.totalAmount.toLocaleString('es-CO')}`,
      },
      {
        Campo: 'Fecha de exportación',
        Valor: new Date().toLocaleString('es-CO'),
      },
      { Campo: 'Filtros aplicados', Valor: metadata.filters || 'Ninguno' },
      { Campo: 'Rango de fechas', Valor: metadata.dateRange || 'Todas' },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 25 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

    const conceptsDetail = payments.flatMap((payment) => {
      if (!payment.concepts || payment.concepts.length === 0) {
        return [{
          'ID Pago': payment.id,
          'Fecha': new Date(payment.createdAt).toLocaleDateString('es-CO'),
          'Acudiente': payment.customerName,
          'Concepto': 'Sin conceptos',
          'ID Estudiante': 'N/A',
          'Monto Concepto': '$0',
        }];
      }

      return payment.concepts.map((concept) => ({
        'ID Pago': payment.id,
        'Fecha': new Date(payment.createdAt).toLocaleDateString('es-CO'),
        'Acudiente': payment.customerName,
        'Concepto': concept.name,
        'ID Estudiante': concept.studentId || 'N/A',
        'Monto Concepto': `$${concept.amount.toLocaleString('es-CO')}`,
      }));
    });

    const conceptsSheet = XLSX.utils.json_to_sheet(conceptsDetail);
    conceptsSheet['!cols'] = [
      { wch: 25 },
      { wch: 18 },
      { wch: 25 },
      { wch: 30 },
      { wch: 20 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(workbook, conceptsSheet, 'Detalle Conceptos');

    const filename = options.filename || this.generateFilename();
    XLSX.writeFile(workbook, filename);
  }


  exportConceptsByStudent(payments: Payment[]): void {
    const studentPayments: Record<string, {
      studentId: string;
      concepts: Array<{
        paymentId: string;
        date: string;
        guardian: string;
        conceptName: string;
        amount: number;
      }>;
      total: number;
    }> = {};

    payments.forEach((payment) => {
      if (!payment.concepts) return;

      payment.concepts.forEach((concept) => {
        const studentId = concept.studentId || 'sin-estudiante';

        if (!studentPayments[studentId]) {
          studentPayments[studentId] = {
            studentId,
            concepts: [],
            total: 0,
          };
        }

        studentPayments[studentId].concepts.push({
          paymentId: payment.id,
          date: new Date(payment.createdAt).toLocaleDateString('es-CO'),
          guardian: payment.customerName,
          conceptName: concept.name,
          amount: concept.amount,
        });

        studentPayments[studentId].total += concept.amount;
      });
    });

    const workbook = XLSX.utils.book_new();

    Object.values(studentPayments).forEach((studentData) => {
      const sheetData = studentData.concepts.map((c) => ({
        Fecha: c.date,
        Acudiente: c.guardian,
        Concepto: c.conceptName,
        Monto: `$${c.amount.toLocaleString('es-CO')}`,
        'ID Pago': c.paymentId,
      }));

      sheetData.push({
        Fecha: '',
        Acudiente: '',
        Concepto: 'TOTAL',
        Monto: `$${studentData.total.toLocaleString('es-CO')}`,
        'ID Pago': '',
      });

      const sheet = XLSX.utils.json_to_sheet(sheetData);
      sheet['!cols'] = [
        { wch: 18 },
        { wch: 25 },
        { wch: 30 },
        { wch: 15 },
        { wch: 25 },
      ];

      const sheetName = studentData.studentId.slice(0, 31); // Excel limita a 31 chars
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    });

    const filename = this.generateFilename('reporte-por-estudiante');
    XLSX.writeFile(workbook, filename);
  }
}

export const exportService = new ExportService();